import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, FormLabel, Grid, Paper, Popover, Stack, Typography, useTheme, alpha } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';
import TextField from '../../textField';
import { examples } from './examples';

type Props = {
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserRquirementsChange: (userRequirements: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepDescribeReq = ({
  userResponses = {},
  maxHeight,
  onUserRquirementsChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const confirm = useConfirm();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [currentExample, setCurrentExample] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const [exAnchorEl, setExAnchorEl] = useState<HTMLElement | null>(null);

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.userRequirements) {
      errors.userRequirements = t('common.errors.requiredField');
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const showExample = (event: React.MouseEvent<HTMLElement>, example: any) => {
    setCurrentExample(example);
    setExAnchorEl(event.currentTarget);
  };

  const hideExample = () => {
    setCurrentExample(null);
    setExAnchorEl(null);
  };

  const handleApplyExample = (example: any) => {
    if (userResponses.userRequirements && userResponses.userRequirements.trim().length > 0) {
      confirm({
        title: 'Overwrite Content?',
        description:
          'You already have something typed in the requirement field. Applying this template will replace your current text. Do you want to continue?',
        confirmationText: 'Overwrite',
        cancellationText: 'Cancel',
        confirmationButtonProps: { variant: 'contained', color: 'primary' },
      })
        .then(() => {
          onUserRquirementsChange(example.text);
        })
        .catch(() => {
          // Do nothing on cancel
        });
    } else {
      onUserRquirementsChange(example.text);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <FormLabel required sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              {'Describe your document/file processing requirement in detail below.'}
            </FormLabel>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Not sure what to write? Click an example below to use it as a template.
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            {examples.map((ex) => (
              <Grid size={{ xs: 12, sm: 6 }} key={ex.name}>
                <Paper
                  variant="outlined"
                  onClick={() => handleApplyExample(ex)}
                  onMouseEnter={(event) => showExample(event, ex)}
                  onMouseLeave={hideExample}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.secondary.main,
                      bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="primary" noWrap>
                    {ex.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {ex.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TextField
            value={userResponses.userRequirements}
            required
            rows={8}
            multiline
            placeholder="e.g., I want to extract data from S3 invoices..."
            error={errors.userRequirements}
            onChange={onUserRquirementsChange}
          />
        </Stack>

        <Popover
          id="example-popover"
          sx={{ pointerEvents: 'none' }}
          open={Boolean(exAnchorEl)}
          anchorEl={exAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          onClose={hideExample}
          disableRestoreFocus
          disablePortal
          slotProps={{
            paper: {
              sx: {
                width: '320px',
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              },
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            {currentExample?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            "{currentExample?.text}"
          </Typography>
        </Popover>
      </Box>

      {/* Action bar */}
      <Box
        ref={actionPanelRef}
        sx={{
          px: 3,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            disabled={!userResponses.userRequirements}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('common.buttons.next')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepDescribeReq;
